import SimpleDataTable from "lib/components/common/simple-data-table";
import SimpleModal from "lib/components/common/simple-modal";
import { DEFAULT_USER_PASSWORD, USER_ROLES } from "lib/constants";
import { useContext, useEffect, useState } from "react";
import { Button, Dropdown, DropdownButton } from "react-bootstrap";
import AddUserForm from "./add-user-form";
import EditUserForm from "./edit-user-form";
import { AppContext } from "lib/contexts/app-context";
import useClinics from "lib/hooks/useClinics";
import useClinicUsers from "lib/hooks/useClinicUsers";
import { USER_COLUMNS } from "lib/constants/table-columns";
import useAuthentication from "lib/hooks/useAuthentication";
import { toast } from "sonner";
import { DEFAULT_CLINIC_USER_FORM_VALUES } from "lib/constants/default-values";

const UserTab = () => {
  const { appState } = useContext(AppContext);
  const {
    resetPassword,
    loading: authLoading,
    error: authError,
  } = useAuthentication();
  const {
    error: clinicUsersError,
    loading: clinicUsersLoading,
    users,
    getUsers,
    addUser,
    updateUser,
  } = useClinicUsers();
  const {
    clinics,
    getClinics,
    loading: clinicsLoading,
    error: clinicsError,
  } = useClinics();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<ClinicUserRoles>(
    USER_ROLES.ADMIN as ClinicUserRoles
  );

  const [selectedUser, setSelectedUser] = useState<ClinicUser | null>(null);

  useEffect(() => {
    getUsers(selectedRole);
    getClinics(true);
  }, []);

  useEffect(() => {
    if (selectedRole) {
      getUsers(selectedRole);
    }
  }, [selectedRole]);

  const onRoleChange = (role: ClinicUserRoles) => {
    setSelectedRole(role);
  };

  const handleRowClick = (row: ClinicUser) => {
    setSelectedUser(row);
    //setIsEditable(true);
    setShowModal(true);
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const onUpdateUser = async (data: CreateUserRequest) => {
    updateUser(data, () => {
      setShowModal(false);
      getUsers(selectedRole);
    });
  };

  const onAddUser = async (values: CreateUserRequest) => {
    const req = { ...values, password: DEFAULT_USER_PASSWORD };
    addUser(req, () => () => {
      getUsers(selectedRole || "Receptionist");
      handleCloseModal();
    });
  };

  const onResetPassword = async (email: string) => {
    resetPassword({ email }, () => {
      setShowModal(false);
      toast.success("Password reset email sent successfully");
    });
  };

  return (
    <>
      <SimpleDataTable
        title="Users"
        columns={USER_COLUMNS}
        error={clinicUsersError || clinicsError || authError}
        data={users ?? []}
        onRowClick={handleRowClick}
        isLoading={clinicUsersLoading || clinicsLoading || authLoading}
        action={
          <div className="d-flex gap-2">
            <DropdownButton
              id="role-dropdown"
              title={selectedRole}
              variant="outline-secondary"
              //style={{ width: "150px" }}
            >
              <Dropdown.Item onClick={() => onRoleChange("Receptionist")}>
                Receptionist
              </Dropdown.Item>
              <Dropdown.Item onClick={() => onRoleChange("Admin")}>
                Admin
              </Dropdown.Item>
            </DropdownButton>
            {appState?.userRole !== "receptionist" && (
              <Button variant="primary" onClick={handleAddUser}>
                Add User
              </Button>
            )}
          </div>
        }
      />

      <SimpleModal
        title={selectedUser ? "Edit User" : "Add User"}
        open={showModal}
        close={handleCloseModal}
        size="lg"
        loading={clinicUsersLoading || clinicsLoading || authLoading}
        error={clinicUsersError || clinicsError || authError}
      >
        {showModal && selectedUser ? (
          <EditUserForm
            clinics={clinics}
            onPasswordReset={() => onResetPassword(selectedUser.email)}
            initialData={selectedUser}
            updateUser={onUpdateUser}
            closeModal={handleCloseModal}
            isLoading={clinicUsersLoading || clinicsLoading}
          />
        ) : showModal ? (
          <AddUserForm
            onAddUser={onAddUser}
            clinics={clinics}
            errorMessage={clinicUsersError || clinicsError}
            DEFAULT_CLINIC_USER_FORM_VALUES={DEFAULT_CLINIC_USER_FORM_VALUES}
          />
        ) : null}
      </SimpleModal>
    </>
  );
};

export default UserTab;
